import React, { useEffect, useState } from "react";
import { Loader2, Mail, MessageSquareText, ShieldCheck, AlertCircle } from "lucide-react";
import { userApi, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";

/** Shared 6-digit code step. `onVerify(code, { trustDevice })` must throw on failure. */
export default function OtpStep({ challenge, setChallenge, onVerify, onBack, title = "Enter verification code", resendPath = "/auth/otp/send", testPrefix = "otp", showTrustDevice = false }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [wait, setWait] = useState(challenge?.resend_after || 30);
  useEffect(() => {
    const t = setInterval(() => setWait((w) => (w > 0 ? w - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (c = code) => {
    if (c.length !== 6) return;
    setBusy(true);
    setErr("");
    try {
      await onVerify(c, { trustDevice });
    } catch (e) {
      setErr(errMsg(e));
      setCode("");
    } finally {
      setBusy(false);
    }
  };
  const resend = async (channel) => {
    setErr("");
    try {
      const r = await userApi.post(resendPath, { challenge_id: challenge.challenge_id, channel });
      setChallenge(r.data);
      setWait(r.data.resend_after || 30);
    } catch (e) {
      setErr(errMsg(e));
    }
  };
  const Icon = challenge.channel === "sms" ? MessageSquareText : Mail;

  return (
    <div data-testid={`${testPrefix}-step`}>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-50"><Icon className="h-5 w-5 text-primary" /></div>
      <h1 className="mt-4 text-2xl font-semibold">{title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        We sent a 6-digit code by {challenge.channel === "sms" ? "SMS" : "email"} to <span className="font-medium text-foreground" data-testid={`${testPrefix}-destination`}>{challenge.destination}</span>. It expires in 10 minutes.
      </p>
      {err && <Alert variant="destructive" className="mt-4" data-testid={`${testPrefix}-error`}><AlertCircle className="h-4 w-4" /><AlertDescription>{err}</AlertDescription></Alert>}
      <div className="mt-6 flex justify-center sm:justify-start">
        <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 6) submit(v); }} data-testid={`${testPrefix}-input`} autoFocus inputMode="numeric">
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11 text-lg" />)}</InputOTPGroup>
        </InputOTP>
      </div>
      <Button className="btn-lift mt-6 h-11 w-full" disabled={busy || code.length !== 6} onClick={() => submit()} data-testid={`${testPrefix}-verify-button`}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}<ShieldCheck className="mr-2 h-4 w-4" /> Verify & continue
      </Button>
      {showTrustDevice && (
        <label className="mt-4 flex cursor-pointer items-start gap-2 rounded-lg border bg-secondary/50 px-3 py-2 text-sm" data-testid={`${testPrefix}-trust-device-label`}>
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-primary"
            data-testid={`${testPrefix}-trust-device-checkbox`}
          />
          <span>
            <span className="font-medium text-foreground">Trust this device for 30 days</span>
            <span className="block text-xs text-muted-foreground">Skip the OTP on this browser for the next 30 days. Don't tick this on a shared or public device.</span>
          </span>
        </label>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <button disabled={wait > 0} onClick={() => resend(challenge.channel)} className="font-medium text-primary disabled:text-muted-foreground" data-testid={`${testPrefix}-resend-button`}>
          {wait > 0 ? `Resend code in ${wait}s` : "Resend code"}
        </button>
        {challenge.channels?.includes("sms") && challenge.channel !== "sms" && <button disabled={wait > 0} onClick={() => resend("sms")} className="font-medium text-primary disabled:text-muted-foreground" data-testid={`${testPrefix}-use-sms`}>Send by SMS instead</button>}
        {challenge.channel === "sms" && challenge.channels?.includes("email") && <button disabled={wait > 0} onClick={() => resend("email")} className="font-medium text-primary disabled:text-muted-foreground" data-testid={`${testPrefix}-use-email`}>Send by email instead</button>}
        {onBack && <button onClick={onBack} className="ml-auto text-muted-foreground hover:text-foreground" data-testid={`${testPrefix}-back`}>Back</button>}
      </div>
      <p className="mt-6 text-xs text-muted-foreground">Never share this code with anyone — our team will never ask for it.</p>
    </div>
  );
}
